import { useState } from 'react'
import { CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { apiService } from '@/services/api'
import { mensajeDeError } from '@/shared/lib/mensajeDeError'
import { formatDate } from '@/shared/lib/dateUtils'
import {
  esAvisoDeSobrecupo,
  estadoDelInterno,
  internoElegido,
  puedeConfirmarInterno,
  type InternoGuardado,
} from '@/features/rh/lib/coberturaDeVacante'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

/**
 * Estado del candidato interno elegido y su confirmación por RH (SRH-313).
 *
 * El solicitante marca al interno y RH lo confirma, como con los externos. Solo
 * el confirmado ocupa una plaza; al cubrirse todas, la vacante se cierra sola.
 * No se le manda nada al interno: solo se avisa a RH.
 *
 * Con la vacante ya cubierta confirmar es sobrecupo: el servidor advierte y
 * aquí se pregunta antes de reintentar.
 */
export const ConfirmacionDeInterno = ({
  jobRequestId,
  internos,
  estatusDeLaVacante,
  permisos,
  onConfirmado,
}: {
  jobRequestId: number
  /** Los internos tal como están guardados, con su confirmación. */
  internos: InternoGuardado[] | undefined
  estatusDeLaVacante: string | undefined
  permisos: string[] | undefined
  onConfirmado: () => void | Promise<void>
}) => {
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sobrecupo, setSobrecupo] = useState<string | null>(null)

  const estado = estadoDelInterno(internos)
  const elegido = internoElegido(internos)
  if (estado === 'sin_elegir' || !elegido) return null

  const puede = puedeConfirmarInterno(permisos, estatusDeLaVacante)

  const confirmar = async () => {
    setGuardando(true)
    setError(null)
    try {
      await apiService.confirmInternalCandidate(jobRequestId, sobrecupo !== null)
      setSobrecupo(null)
      await onConfirmado()
    } catch (fallo) {
      if (esAvisoDeSobrecupo(fallo)) {
        setSobrecupo((fallo as { message?: string }).message ?? 'La vacante ya tiene las plazas cubiertas.')
      } else {
        setError(mensajeDeError(fallo))
      }
    } finally {
      setGuardando(false)
    }
  }

  if (estado === 'confirmado') {
    return (
      <p className="mt-3 flex items-center gap-2 text-sm text-[#15803D]">
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        {elegido.name} está confirmado por RH
        {elegido.confirmedAt ? ` el ${formatDate(elegido.confirmedAt)}` : ''}. Ocupa una
        plaza de la vacante.
      </p>
    )
  }

  return (
    <div className="mt-3 rounded-lg bg-[#FEF6E2] px-3 py-2.5 text-sm text-[#795E1B]">
      <p className="flex items-start gap-2">
        <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          {elegido.name} fue elegido por el solicitante y falta que RH lo confirme. Hasta
          entonces no ocupa una plaza.
        </span>
      </p>

      {sobrecupo && (
        <p role="alert" className="mt-2 font-medium">
          {sobrecupo}
        </p>
      )}
      {error && <p className="mt-2 text-red-600">{error}</p>}

      {puede && (
        <button
          type="button"
          onClick={() => void confirmar()}
          disabled={guardando}
          className={estiloDeBoton('primario', 'mt-3')}
        >
          {guardando && <Loader2 className="w-4 h-4 animate-spin" />}
          {sobrecupo ? 'Confirmar de todos modos' : 'Confirmar selección'}
        </button>
      )}
    </div>
  )
}
